"use client";

import { useEffect, useState } from "react";
import { fetchAllNeighborhoodData } from "./serverDataFetcher";

/**
 * Client-side data hydration component
 * This component runs ONLY in the browser after the static HTML is loaded
 * It fetches fresh GraphQL data in the background
 *
 * NOTE: This doesn't update the server component directly.
 * Instead, it stores fresh data that interactive client components can access.
 * For displaying GraphQL data, child components should be made client components
 * that listen for this hydration.
 */
export default function ClientDataHydration({ nhSlug, initialData }) {
  const [isHydrating, setIsHydrating] = useState(false);
  const [hydrationComplete, setHydrationComplete] = useState(false);

  useEffect(() => {
    // Only run on client side
    if (typeof window === "undefined") return;

    // Only fetch if we have initial data to compare against
    if (!initialData || !nhSlug) return;

    // Don't fetch if already hydrating or complete
    if (isHydrating || hydrationComplete) return;

    // Fetch GraphQL data in the background
    const hydrateWithGraphQL = async () => {
      try {
        setIsHydrating(true);
        console.log(`🔄 [CLIENT] Starting GraphQL hydration for: ${nhSlug}`);

        const graphqlData = await fetchAllNeighborhoodData(nhSlug);

        if (graphqlData && graphqlData?.result && graphqlData?.result?.data) {
          console.log(`✅ [CLIENT] GraphQL data fetched successfully`);

          // Merge GraphQL data with initial JSON data
          const mergedData = {
            ...initialData.data,
            ...graphqlData.result.data,
            // Ensure all required fields exist
            getneighborhoodreview: graphqlData.result.data?.getneighborhoodreview || initialData.data?.getneighborhoodreview || [],
            getSchoolinNh: graphqlData.result.data?.getSchoolinNh || initialData.data?.getSchoolinNh || [],
            getKeywodSummary: graphqlData.result.data?.getKeywodSummary || initialData.data?.getKeywodSummary || [],
            getquestions: graphqlData.result.data?.getquestions || initialData.data?.getquestions || [],
            neighborhubData: graphqlData.result.data?.neighborhubData || initialData.data?.neighborhubData || [],
            getDetailsNhBoundriesnh: graphqlData.result.data?.getDetailsNhBoundriesnh || initialData.data?.getDetailsNhBoundriesnh || []
          };

          // Store the hydrated data in window object for access by other components
          if (typeof window !== "undefined") {
            window.__NH_HYDRATED_DATA__ = {
              nhSlug,
              data: mergedData,
              timestamp: Date.now()
            };

            // Dispatch custom event so components can listen and update
            const event = new CustomEvent('nh-data-hydrated', {
              detail: { nhSlug, data: mergedData }
            });
            window.dispatchEvent(event);
          }

          setHydrationComplete(true);
          console.log(`✅ [CLIENT] Hydration complete and broadcasted for: ${nhSlug}`);
        } else {
          console.log(`⚠️  [CLIENT] GraphQL returned no data, using initial JSON data`);
          setHydrationComplete(true);
        }
      } catch (err) {
        console.error(`❌ [CLIENT] GraphQL hydration failed:`, err);
        // Keep using initial JSON data on error
        setHydrationComplete(true);
      } finally {
        setIsHydrating(false);
      }
    };

    // Start hydration after a small delay to ensure initial render is complete
    const timer = setTimeout(() => {
      hydrateWithGraphQL();
    }, 100);

    return () => clearTimeout(timer);
  }, [nhSlug, initialData, isHydrating, hydrationComplete]);

  // Optionally show a small loading indicator while hydrating
  if (isHydrating) {
    return (
      <div style={{ display: 'none' }} data-hydrating="true" aria-hidden="true">
        Updating with latest data...
      </div>
    );
  }

  return null;
}
